import type { AttendingTaskRow } from '../data/attendingTasks';
import { TASK_EMPTY_DISPLAY, taskStatusKeyFromApi } from '../data/taskStatus';
import { formatDisplayDateTime } from '../utils/dateFormat';
import { displayOrDash, parseUrgencyLevel, type ApiTaskRow } from './taskListAdapter';

/** CMS attending-task list item → table row. */
export function adaptAttendingTaskRow(row: ApiTaskRow & Record<string, unknown>): AttendingTaskRow {
  const caseRequestId = strField(row, 'caseRequestId', 'case_request_id', 'id');
  const userTaskId = strField(row, 'taskId', 'task_id');
  const id = caseRequestId || strField(row, 'reference_no', 'file_ref_no', 'fileRefNo');
  const statusName = strField(row, 'taskStatus', 'task_status', 'statusName', 'status_name');
  const statusKey = statusName
    ? taskStatusKeyFromApi(strField(row, 'statusCode', 'status_code'), statusName)
    : 'pending';
  const timeLabel = displayOrDash(
    strField(row, 'timeRemainingDisplay', 'time_remaining_display', 'timeRemainingLabel', 'time_remaining_label'),
  );
  const isOverdue = row.isOverdue === true || row.is_overdue === true || /overdue/i.test(timeLabel);
  const submittedRaw = strField(row, 'submittedAt', 'submitted_at');

  return {
    id: id || caseRequestId,
    userTaskId: userTaskId || undefined,
    caseRequestId: caseRequestId || id,
    ref: displayOrDash(strField(row, 'file_ref_no', 'fileRefNo', 'reference_no')),
    fileCategory: displayOrDash(strField(row, 'fileCategory', 'file_category')),
    subFileCategory: displayOrDash(strField(row, 'subFileCategory', 'sub_file_category')),
    agency: displayOrDash(strField(row, 'submittedAgency', 'submitted_agency')),
    submittedAt: displayOrDash(formatDisplayDateTime(submittedRaw)),
    submittedAtIso: submittedRaw.length >= 10 ? submittedRaw.slice(0, 10) : '',
    caseNumber: displayOrDash(strField(row, 'caseNumber', 'case_number')),
    stage: displayOrDash(
      strField(row, 'currentWorkflowStage', 'current_workflow_stage', 'workflowStageName', 'workflow_stage_name'),
    ),
    statusKey,
    status: statusName ? statusName : TASK_EMPTY_DISPLAY,
    timeLabel,
    isOverdue,
    hasTimeValue: timeLabel !== TASK_EMPTY_DISPLAY,
    urgencyLevel: parseUrgencyLevel(strField(row, 'urgencyLevel', 'urgency_level')),
    assignedOfficer: displayOrDash(
      strField(
        row,
        'assignedUserName',
        'assigned_user_name',
        'assignedOfficerName',
        'assigned_officer_name',
        'assignedOfficer',
        'assigned_officer',
      ),
    ),
    supervisor: displayOrDash(
      strField(row, 'supervisorName', 'supervisor_name', 'supervisor', 'assignedSupervisorName', 'assigned_supervisor_name'),
    ),
    hasNewNotification: row.hasNewNotification === true || row.has_new_notification === true,
  };
}

function strField(row: ApiTaskRow & Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = row[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}
